/**
 * The bout bot and armour / knock-downs (lane DUEL-ARMOR; rules systems/duelArmor.ts, systems/duelKnock.ts). Kept out of
 * systems/duelBot.ts (one gate in `BOT_SKILL_GATES`, like duelBotLock):
 * - it does not trade into armour: against red no attack, break or lock move counts as ready (it steps out instead, by
 *   level); against yellow no plain attack does (a break, the pierce move or the guard answer it);
 * - against a body on the ground only low moves count (the rest would pass over it);
 * - downed itself, it picks a get-up by level: low levels mostly rise, higher ones mix rise guarding, the roll, the rising
 *   attack and staying down, and lean away from what the player punished last time.
 * Its draws come from a hash of the world tick and the bot's id (the bot brain's draws stay as they were).
 */
import { ARMOR_PIERCE } from '../data/duelArmor';
import { OTG_MOVES } from '../data/duelKnock';
import { SKILLS } from '../data/skills';
import { hash2 } from '../rng';
import { botSeatOf } from './botSeat';
import { BOT_SKILL_GATES } from './duelBot';
import { duelArmorOf } from './duelArmor';
import { duelKnockOf, type GetUp } from './duelKnock';
import { duelMatchOf } from './duelMatch';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelBotArmorSystem>();
export const duelBotArmorOf = (w: object): DuelBotArmorSystem | null => SYSTEMS.get(w) ?? null;

/** Chance (by level 0-4) the bot steps out of a red wind-up in reach, once per window. */
export const BOT_RED_STEP: readonly number[] = [0.15, 0.3, 0.45, 0.55, 0.65];
/** Get-up weights by level: rise, guard, roll, attack, stay. */
export const BOT_GETUP: readonly (readonly [number, number, number, number, number])[] = [
  [0.85, 0.15, 0, 0, 0],
  [0.6, 0.3, 0.1, 0, 0],
  [0.3, 0.3, 0.25, 0.15, 0],
  [0.2, 0.25, 0.25, 0.2, 0.1],
  [0.15, 0.25, 0.25, 0.2, 0.15],
];
const CHOICES: readonly GetUp[] = ['rise', 'guard', 'roll', 'attack', 'stay'];

function seat(w: object) {
  const st = botSeatOf(w) ?? duelMatchOf(w)?.current ?? null;
  return st && !st.pvp && st.phase === 'fight' ? st : null;
}

BOT_SKILL_GATES.push((w, botId, id) => {
  const st = seat(w);
  if (!st) return true;
  const foeId = botId === st.foeId ? st.meId : st.foeId;
  const s = SKILLS[id];
  if (!s) return true;
  const knock = duelKnockOf(w);
  if (knock?.isDown(foeId))
    return (
      OTG_MOVES.includes(id) || !!s.ground || (!!s.aoe && !s.range) || s.kind === 'buff' || s.kind === 'heal'
    );
  const tier = duelArmorOf(w)?.tier(foeId) ?? null;
  if (tier === 'red') return s.kind !== 'attack' && s.kind !== 'break' && s.kind !== 'power';
  if (tier === 'yellow') return s.kind !== 'attack' || ARMOR_PIERCE.includes(id);
  return true;
});

export class DuelBotArmorSystem implements WorldSystem {
  readonly name = 'duelBotArmor';
  off = false;
  private n = 0;
  /** The red window already answered (bot id -> the move), and the knock-down already decided. */
  private readonly redSeen = new Map<number, string>();
  private readonly downSeen = new Set<number>();
  /** What the player did to the bot's last get-up (a hit as it rose): the bot leans away from that get-up next time. */
  private lastGetUp: GetUp | null = null;
  private lastPunished = false;
  /** The bot's latest get-up pick (tests). */
  latest: GetUp | null = null;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  step(_dt: number): void {
    const w = this.h.world;
    const st = seat(w);
    if (this.off || !st || st.paused || !w.pvpBout?.fight) return;
    const bot = w.get(st.foeId);
    const foe = w.get(st.meId);
    if (!bot || !foe || bot.dead || foe.dead) return;
    const rnd = () => hash2(Math.round(w.time * 60), bot.id * 1291 + this.n++);
    const lv = Math.max(0, Math.min(4, st.level));
    const knock = duelKnockOf(w);
    // Downed: pick a get-up once.
    if (knock?.isDown(bot.id)) {
      if (!this.downSeen.has(bot.id)) {
        this.downSeen.add(bot.id);
        const wts = [...BOT_GETUP[lv]!];
        if (lv >= 3 && this.lastPunished && this.lastGetUp) wts[CHOICES.indexOf(this.lastGetUp)]! *= 0.3;
        const sum = wts.reduce((a, b) => a + b, 0);
        let r = rnd() * sum;
        let pick: GetUp = 'rise';
        for (let i = 0; i < CHOICES.length; i++) {
          r -= wts[i]!;
          if (r <= 0) {
            pick = CHOICES[i]!;
            break;
          }
        }
        this.latest = pick;
        this.lastGetUp = pick;
        this.lastPunished = false;
        if (pick !== 'rise') knock.choose(bot.id, pick);
      }
      return;
    }
    if (this.downSeen.has(bot.id)) {
      // Rising or just up: a blow landing now counts as the player punishing that get-up.
      const v = knock?.view(bot.id);
      if (!v) this.downSeen.delete(bot.id);
      else if (bot.hitT > 0.1) this.lastPunished = true;
    }
    // Red in reach: step out, once per window.
    const a = duelArmorOf(w)?.view(foe.id);
    if (a?.tier === 'red') {
      const d = Math.hypot(foe.x - bot.x, foe.y - bot.y);
      if (this.redSeen.get(bot.id) !== a.skillId && d < 4.5) {
        this.redSeen.set(bot.id, a.skillId);
        if (rnd() < BOT_RED_STEP[lv]! && bot.stunT <= 0) {
          const l = d || 1;
          w.apply({ t: 'dash', dx: (bot.x - foe.x) / l, dy: (bot.y - foe.y) / l }, bot.id);
        }
      }
    } else this.redSeen.delete(bot.id);
  }
}
